import { useCallback } from "react";
import { analyzeSmartViewBlockSourceSync } from "./backend-api";
import { logger } from "@/modules/logger";
import { useAppStore } from "@/store";

// Lightweight notifier used by block components to ask the BE agent to check
// whether a direct Smart View edit should be reflected back into source notes.
// This intentionally avoids chat; only failures or needed user intervention
// should bubble up visibly.
export const useNotifyAgent = (smartViewId: string) => {
  const { store } = useAppStore();

  return useCallback(
    async (params: {
      blockId: string;
      blockTitle: string;
      summary: string;
      sourceNoteIds?: string[];
    }) => {
      const sourceNoteIds = dedupeStrings(params.sourceNoteIds ?? []);
      try {
        await analyzeSmartViewBlockSourceSync({
          smartViewId,
          blockId: params.blockId,
          blockTitle: params.blockTitle,
          summary: params.summary,
          sourceNoteIds,
          sourceNoteTitles: sourceNoteIds.map(noteId => getNoteTitle(store, noteId)),
        });
      } catch (err) {
        logger.warn({
          message: "Smart View: failed to notify agent of user action",
          info: { err: String(err) },
        });
      }
    },
    [smartViewId, store]
  );
};

const dedupeStrings = (values: string[]): string[] => {
  return Array.from(new Set(values.map(value => value.trim()).filter(value => value.length > 0)));
};

const getNoteTitle = (store: ReturnType<typeof useAppStore>["store"], noteId: string): string => {
  const note = store.notes.getNoteObservableById({ noteId });
  return note?.suggestionData?.suggested_primary_label || note?.title || "Source note";
};
